import { useEffect, useState } from 'react'

import { Coupon, Offerings, Subscription } from '@infura/packages.api'
import { ServiceTier, V1Service } from '@infura/packages.uks'

import { PaymentFormState } from 'components/billing/PaymentFormState'
import { CheckoutContextProvider } from 'components/checkout/Context'
import PaymentDetails from 'components/checkout/PaymentDetails'
import SubscriptionSummary from 'components/checkout/SubscriptionSummary'

import useCoupon from 'hooks/useCoupon'
import useSubscription from 'hooks/useSubscription'
import useUser from 'hooks/useUser'

import { FormBackground, isChargifyCouponUsedUp as isCouponUsedUp } from 'lib/utils/chargify'

interface CheckoutProps {
  offerings: Offerings
  selectedPlan: ServiceTier
  checkoutCloseCallback?: () => void
  currentRequestPacksQuantity?: number
  formBackground?: FormBackground
  className?: string
  showPaymentFormCancelButton?: boolean
  requestPacksQuantity?: number
  servicePlan?: V1Service
  onSubscriptionUpdated?: (formValues: PaymentFormState, subscription?: Subscription) => void

  throughputAddonEnabled: boolean
  currentThroughputAddonEnabled: boolean
}

export const sessionStoragePromoCode = 'subscription-summary-promo-code'

export const Checkout = ({
  checkoutCloseCallback,
  className,
  currentRequestPacksQuantity = 0,
  currentThroughputAddonEnabled,
  formBackground = 'default',
  offerings,
  onSubscriptionUpdated,
  requestPacksQuantity = 0,
  selectedPlan,
  servicePlan,
  showPaymentFormCancelButton = true,
  throughputAddonEnabled
}: CheckoutProps) => {
  const { subscription } = useSubscription()

  const { coupon: existingCoupon, loading: couponLoading } = useCoupon(
    isCouponUsedUp(subscription?.coupons, subscription?.coupon_codes[0]) ? null : subscription?.coupon_codes[0]
  )

  const [isPaymentFormVisible, setIsPaymentFormVisible] = useState<boolean>(false)
  const [coupon, setCoupon] = useState<Coupon | undefined>()
  const { mutate: revalidateUser, user } = useUser()

  useEffect(() => {
    if (!couponLoading) {
      setCoupon(existingCoupon)
    }
  }, [existingCoupon, couponLoading])

  useEffect(() => {
    const couponFromSessionStorage = sessionStorage.getItem(sessionStoragePromoCode)
    if (couponFromSessionStorage) {
      setCoupon(JSON.parse(couponFromSessionStorage))
    }
  }, [])

  return (
    <CheckoutContextProvider
      value={{
        checkoutCloseCallback,
        coupon,
        couponLoading,
        currentRequestPacksQuantity,
        currentThroughputAddonEnabled,
        formBackground,
        requestPacksQuantity,
        revalidateUser,
        selectedPlan,
        servicePlan,
        setCoupon,
        showPaymentFormCancelButton,
        throughputAddonEnabled,
        user
      }}
    >
      <div className={className}>
        <SubscriptionSummary
          isPaymentFormVisible={isPaymentFormVisible}
          offerings={offerings}
          onSubscriptionUpdated={onSubscriptionUpdated}
        />
        <PaymentDetails isPaymentFormVisible={isPaymentFormVisible} setIsPaymentFormVisible={setIsPaymentFormVisible} />
      </div>
    </CheckoutContextProvider>
  )
}
